// src/lib/notifications.ts
import { supabase } from './supabase';
import { getCurrentUser } from './auth';

/* --------------------------------- Retry --------------------------------- */
async function withRetry<T>(
  operation: () => Promise<T>,
  maxRetries = 3,
  delay = 800
): Promise<T> {
  let lastErr: unknown;
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await operation();
    } catch (err: any) {
      lastErr = err;
      const isNetwork =
        err?.name === 'TypeError' && /Failed to fetch|NetworkError|fetch failed/i.test(err?.message || '');
      const isAbort = err?.name === 'AbortError';

      if (attempt === maxRetries || (!isNetwork && !isAbort)) break;
      await new Promise(r => setTimeout(r, delay * attempt)); // simple backoff
    }
  }
  throw lastErr instanceof Error ? lastErr : new Error('Request failed');
}

/* ------------------------------- Types/Model ------------------------------ */
export type NotificationType =
  | 'message'
  | 'friend_request'
  | 'friend_accepted'
  | 'new_song'
  | 'new_story';

export interface Notification {
  id: string;
  user_id: string;
  type: NotificationType;
  title: string;
  message: string;
  is_read: boolean;
  related_id: string | null;
  related_user_id: string | null;
  created_at: string;
  related_user?: {
    id?: string;
    email: string;
    full_name: string | null;
    profile_image: string | null;
  } | null;
}

/* ----------------------------- Helper: userId ----------------------------- */
function resolveUserId(arg1?: number | string, arg2?: number) {
  // Supports both signatures:
  //   getUserNotifications(20)
  //   getUserNotifications(userId, 20)
  let userId: string | undefined;
  let limit = 20;

  if (typeof arg1 === 'string') {
    userId = arg1;
    limit = arg2 ?? 20;
  } else if (typeof arg1 === 'number') {
    limit = arg1;
  }

  if (!userId) {
    const user = getCurrentUser();
    userId = user?.id;
  }

  return { userId, limit };
}

/* -------------------------- Get user notifications ------------------------ */
export const getUserNotifications = async (
  arg1?: number | string,
  arg2?: number
): Promise<Notification[]> => {
  const { userId, limit } = resolveUserId(arg1, arg2);
  if (!userId) {
    console.warn('No user found for notifications');
    return [];
  }

  try {
    // --- Attempt 1: One query with PostgREST FK join (fastest) ---
    const joined = await withRetry(async () => {
      const res = await supabase
        .from('stripe_notice_users')
        .select(
          `
          id, user_id, type, title, message, is_read, related_id, related_user_id, created_at,
          related_user:stripeusers!stripe_notice_users_related_user_id_fkey(id, email, full_name, profile_image)
        `
        )
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (res.error) throw res.error;
      return res.data as any[];
    });

    // Normalize shape
    return (joined || []).map((n) => ({
      ...n,
      related_user: n.related_user ?? null,
    })) as Notification[];
  } catch (joinErr) {
    console.warn('FK join not available, falling back to 2-step fetch:', joinErr);

    // --- Attempt 2: Fallback to 2 queries (no join capability) ---
    try {
      const { data: baseList } = await withRetry(async () => {
        const res = await supabase
          .from('stripe_notice_users')
          .select('*')
          .eq('user_id', userId)
          .order('created_at', { ascending: false })
          .limit(limit);
        if (res.error) throw res.error;
        return res;
      });

      const notifications: Notification[] = Array.isArray(baseList) ? (baseList as any) : [];
      if (notifications.length === 0) return [];

      const relatedIds = [
        ...new Set(
          notifications
            .map((n) => n.related_user_id)
            .filter((x): x is string => Boolean(x))
        ),
      ];

      if (relatedIds.length === 0) return notifications;

      const { data: users } = await withRetry(async () => {
        const res = await supabase
          .from('stripeusers')
          .select('id, email, full_name, profile_image')
          .in('id', relatedIds);
        if (res.error) throw res.error;
        return res;
      });

      const map = new Map((users || []).map((u) => [u.id, u]));
      return notifications.map((n) => ({
        ...n,
        related_user: n.related_user_id ? map.get(n.related_user_id) ?? null : null,
      }));
    } catch (error) {
      console.error('Error fetching notifications (fallback):', error);
      return [];
    }
  }
};

/* --------------------------- Get unread count ----------------------------- */
export const getUnreadNotificationCount = async (
  maybeUserId?: string
): Promise<number> => {
  const userId = maybeUserId || getCurrentUser()?.id;
  if (!userId) return 0;

  try {
    const { count } = await withRetry(async () => {
      const res = await supabase
        .from('stripe_notice_users')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('is_read', false);
      if (res.error) throw res.error;
      return res;
    });

    return count ?? 0;
  } catch (error) {
    console.error('Error fetching unread count:', error);
    return 0;
  }
};

/* ---------------------------- Mark as read (1) ---------------------------- */
export const markNotificationAsRead = async (notificationId: string): Promise<void> => {
  await withRetry(async () => {
    const res = await supabase
      .from('stripe_notice_users')
      .update({ is_read: true })
      .eq('id', notificationId)
      .select('id')
      .single();

    if (res.error) throw res.error;
    return res;
  });
};

/* -------------------------- Mark all as read (user) ----------------------- */
export const markAllNotificationsAsRead = async (): Promise<void> => {
  const userId = getCurrentUser()?.id;
  if (!userId) throw new Error('User not authenticated');

  await withRetry(async () => {
    const res = await supabase.rpc('mark_all_notifications_read', { p_user_id: userId });
    if (res.error) throw res.error;
    return res;
  });
};

/* ------------------------------ Create notice ----------------------------- */
export const createNotification = async (
  userId: string,
  type: NotificationType,
  title: string,
  message: string,
  relatedId?: string,
  relatedUserId?: string
): Promise<string> => {
  const { data, error } = await withRetry(async () => {
    const res = await supabase.rpc('create_notification', {
      p_user_id: userId,
      p_type: type,
      p_title: title,
      p_message: message,
      p_related_id: relatedId ?? null,
      p_related_user_id: relatedUserId ?? null,
    });
    if (res.error) throw res.error;
    return res;
  });

  if (error) throw error;
  // Supabase RPC returns { data: <uuid> }
  return (data as unknown as string) || '';
};

/* ------------------------------- Realtime sub ----------------------------- */
export const subscribeToNotifications = (
  userId: string,
  onNotification: (notification: Notification) => void
) => {
  const channel = supabase
    .channel(`notifications-${userId}`)
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'stripe_notice_users',
        filter: `user_id=eq.${userId}`,
      },
      async (payload) => {
        const raw = payload.new as any;

        const newNotification: Notification = {
          id: raw.id,
          user_id: raw.user_id,
          type: raw.type,
          title: raw.title,
          message: raw.message,
          is_read: raw.is_read,
          related_id: raw.related_id,
          related_user_id: raw.related_user_id,
          created_at: raw.created_at,
          related_user: null,
        };

        // Try to hydrate related_user (best-effort)
        if (newNotification.related_user_id) {
          try {
            const { data: relatedUser } = await supabase
              .from('stripeusers')
              .select('id, email, full_name, profile_image')
              .eq('id', newNotification.related_user_id)
              .single();
            if (relatedUser) newNotification.related_user = relatedUser;
          } catch {
            // ignore — we still push the base notification
          }
        }

        onNotification(newNotification);
      }
    )
    .subscribe();

  return () => {
    try {
      supabase.removeChannel(channel);
    } catch {
      // ignore
    }
  };
};

/* ------------------------------- UI helpers ------------------------------- */
export const getNotificationIcon = (type: NotificationType): string => {
  switch (type) {
    case 'message':
      return '💬';
    case 'friend_request':
      return '👥';
    case 'friend_accepted':
      return '🤝';
    case 'new_song':
      return '🎵';
    case 'new_story':
      return '📖';
    default:
      return '🔔';
  }
};

export const getNotificationColor = (type: NotificationType): string => {
  switch (type) {
    case 'message':
      return 'text-blue-400';
    case 'friend_request':
      return 'text-yellow-400';
    case 'friend_accepted':
      return 'text-green-400';
    case 'new_song':
      return 'text-purple-400';
    case 'new_story':
      return 'text-indigo-400';
    default:
      return 'text-gray-400';
  }
};

export const formatNotificationTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

  if (diffInMinutes < 1) return 'Just now';
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`;

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `${diffInHours}h ago`;

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) return `${diffInDays}d ago`;

  return date.toLocaleDateString();
};
